import { useCallback, useMemo, useRef, useState } from "react";
import { useClickInterception, useUnsavedChangesWarning } from "@repo/shared-components";
import { Spinner } from "@repo/ui";
import type { CreateStatusRequest, Status } from "~/types/api";
import { useWorkflowCreationContext } from "../../../workflow-creation-context-provider";
import { WorkflowsDialogFooter } from "../workflows-dialog-footer";
import { useStatusView } from "./hooks/use-status-view";
import { StatusInput } from "./status-input";
import { WorkflowsTable, type TableColumn } from "../workflows-table/workflows-table";

export type StatusUnion = CreateStatusRequest | Status;

export type StatusRow = {
  id: string;
  statusCode: number;
  name: string;
  actionsCount: number | undefined;
  originalObj: StatusUnion;
};

type Props = {
  isEditMode?: boolean;
  workflowId?: number;
  onClose?: () => void;
};

const tableColumns: TableColumn<StatusRow>[] = [
  { label: "Status No.", key: "statusCode" },
  { label: "Status Name", key: "name" },
  { label: "Actions", key: "actionsCount" },
];

export const StatusView = ({ isEditMode = false, workflowId, onClose }: Props) => {
  const {
    state: { statuses: workflowStatuses, actions: allActions, committedStatuses, committedActions },
    updateState: updateWorkflowState,
  } = useWorkflowCreationContext();

  const statusContainerRef = useRef<HTMLDivElement>(null);
  const [statusUnsaved, setStatusUnsaved] = useState<{
    hasChanges: boolean;
    onDiscard: () => void;
  }>({ hasChanges: false, onDiscard: () => {} });

  const handleStatusUnsavedChanges = useCallback((hasChanges: boolean, onDiscard: () => void) => {
    setStatusUnsaved({ hasChanges, onDiscard });
  }, []);

  const { showDiscardDialog } = useUnsavedChangesWarning({
    hasUnsavedChanges: statusUnsaved.hasChanges,
    onDiscard: statusUnsaved.onDiscard,
  });

  useClickInterception(statusContainerRef, statusUnsaved.hasChanges, showDiscardDialog);

  const handleStatusDeleted = useCallback(
    (statusCode: number) => {
      const unlinkedStatusToAction = allActions.map((a) => ({
        ...a,
        statusCodes: (a.statusCodes ?? []).filter((code) => code !== statusCode),
      }));
      updateWorkflowState("actions", unlinkedStatusToAction);
    },
    [allActions, updateWorkflowState]
  );

  const hasActionChanges = useMemo(() => {
    if (isEditMode) return false;
    return JSON.stringify(committedActions) !== JSON.stringify(allActions);
  }, [allActions, committedActions, isEditMode]);

  const {
    data,
    selectedStatus,
    takenCodes,
    changesSummary,
    selectedIndex,
    setSelectedIndex,
    isPending,
    isLoadingStatuses,
    handleOnClickAdd,
    handleInputSave,
    handleDelete,
    handleSaveGlobal,
  } = useStatusView({
    isEditMode,
    workflowId,
    workflowStatuses,
    committedStatuses,
    allActions,
    onStatusDeleted: handleStatusDeleted,
  });

  const dropdownMenuOptions = [
    {
      id: "delete",
      label: "Delete Item Status",
      onClick: handleDelete,
    },
  ];

  if (isEditMode && isLoadingStatuses) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <WorkflowsTable
          className="h-80 max-h-80 w-full"
          columns={tableColumns}
          data={data}
          onClickAdd={handleOnClickAdd}
          selectedIndex={selectedIndex}
          onSelectRow={setSelectedIndex}
          addButtonLabel="Create new Item Status"
          dropdownMenuItems={dropdownMenuOptions}
        />
        <div ref={statusContainerRef}>
          <StatusInput
            key={selectedStatus ? `${selectedStatus.code}-${selectedIndex}` : "empty"}
            takenCodes={takenCodes}
            status={selectedStatus}
            onSave={handleInputSave}
            onHasUnsavedChanges={handleStatusUnsavedChanges}
          />
        </div>
      </div>
      <WorkflowsDialogFooter
        onCancel={() =>
          showDiscardDialog(() => {
            if (isEditMode) {
              onClose?.();
            } else {
              updateWorkflowState("statuses", committedStatuses);
              updateWorkflowState("currentView", "default");
            }
          })
        }
        onSave={async () => {
          await handleSaveGlobal(onClose, updateWorkflowState);
          if (!isEditMode) {
            updateWorkflowState("committedActions", allActions);
          }
        }}
        disableSave={(!changesSummary.hasChanges && !hasActionChanges) || isPending}
        loading={isPending}
      />
    </>
  );
};
